import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createTaxesT } from '../../../utils/taxesTranslations'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Box } from '../../atoms/Box'
import { Icon } from '../../atoms/Icon'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import {
  TaxContainer,
  ContentContainer,
  LoadingContainer,
  ErrorContainer,
  FilterSection,
  FilterBar,
  LocationFilters,
  LocationBadge,
  YearSelectorWrapper,
  YearLabel,
  YearSelect,
} from './TaxContent.styles'

export interface TaxContentProps {
  data: any
  loading: boolean
  error: string | null
  activeTab: string
  onTabChange: (tab: string) => void
  selectedYear: number | null
  onYearChange: (year: number) => void
  availableYears: number[]
  renderTabContent: () => React.ReactNode
  locationName?: string
  cantonName?: string
  readOnly?: boolean
}

export const TaxContent: React.FC<TaxContentProps> = ({
  data,
  loading,
  error,
  activeTab,
  onTabChange,
  selectedYear,
  onYearChange,
  availableYears,
  renderTabContent,
  locationName,
  cantonName,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createTaxesT(rawT, i18n)

  const taxTabs = useMemo(
    () => [
      { id: 'overview', label: t('tabs.overview'), icon: 'bar-chart-2' },
      { id: 'trends', label: t('tabs.trends'), icon: 'trending-up' },
    ],
    [t]
  )

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <Box>{t('states.loading')}</Box>
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer>
          <Box>{t('states.error', { error })}</Box>
        </ErrorContainer>
      )
    }

    if (!data) {
      return (
        <LoadingContainer>
          <Box>{t('states.noData')}</Box>
        </LoadingContainer>
      )
    }

    return renderTabContent()
  }

  return (
    <TaxContainer>
      <AnalysisSectionHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Filter Section with Year Selector */}
      <FilterSection>
        <FilterBar>
          <LocationFilters>
            {/* Location Badge */}
            {locationName && (
              <LocationBadge>
                <Icon name="map-pin" size="small" />
                {locationName}
                {cantonName && ` (${cantonName})`}
              </LocationBadge>
            )}

            {/* Year Selector - only show on overview tab */}
            {activeTab === 'overview' &&
              availableYears.length > 0 &&
              !readOnly && (
                <YearSelectorWrapper>
                  <YearLabel>{t('filters.year')}:</YearLabel>
                  <YearSelect
                    value={selectedYear || ''}
                    onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </YearSelect>
                </YearSelectorWrapper>
              )}
          </LocationFilters>
        </FilterBar>
      </FilterSection>

      <AnalysisTabButtonMenu
        tabs={taxTabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      <ContentContainer>{renderContent()}</ContentContainer>
    </TaxContainer>
  )
}
